'use client'

import React, { memo, useEffect, useMemo, useRef } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import { HiOutlineMagnifyingGlass } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import Spinner from '@/components/common/Spinner'

import type { RecipientSearchResult } from '@/types/recipient-search'

import RecipientCard from './RecipientCard'

interface RecipientResultsProps {
  results: RecipientSearchResult[]
  total: number
  isCountKnown?: boolean
  isLoading: boolean
  isRefetching?: boolean
  isFetchingNextPage?: boolean
  hasNextPage?: boolean
  onLoadMore?: () => void
  pinnedResult?: RecipientSearchResult | null
  className?: string
}

/**
 * Empty state component
 */
const EmptyState: React.FC<{ query?: string }> = memo(({ query }) => (
  <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
    <div className="w-14 h-14 mb-5 rounded-xl bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-800 dark:to-primary-900 flex items-center justify-center shadow-sm">
      <HiOutlineMagnifyingGlass className="w-7 h-7 text-primary-400 dark:text-primary-500" />
    </div>
    <h3 className="text-base font-semibold text-primary-900 dark:text-primary-100 mb-1.5">
      No recipients found
    </h3>
    <p className="text-sm text-primary-500 dark:text-primary-400 max-w-sm leading-relaxed">
      {query
        ? `No results for "${query}". Try adjusting your search or filters.`
        : 'Search for a company name, UEI, or try adjusting your filters.'}
    </p>
  </div>
))

EmptyState.displayName = 'EmptyState'

/**
 * Loading skeleton for recipient cards - updated to match new card design
 */
const RecipientSkeleton: React.FC = memo(() => (
  <div className="w-full rounded-lg border border-l-[3px] border-primary-200 dark:border-primary-700 border-l-primary-200 dark:border-l-primary-700 bg-white dark:bg-primary-900 overflow-hidden animate-pulse">
    <div className="p-4">
      {/* Top row: name + spending */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1">
          <div className="h-5 w-2/3 bg-primary-100 dark:bg-primary-800 rounded mb-2" />
          <div className="h-3 w-1/3 bg-primary-100 dark:bg-primary-800 rounded" />
        </div>
        <div className="text-right">
          <div className="h-6 w-20 bg-primary-100 dark:bg-primary-800 rounded mb-1" />
          <div className="h-2 w-14 bg-primary-50 dark:bg-primary-800/50 rounded ml-auto" />
        </div>
      </div>

      {/* Badges row */}
      <div className="flex gap-1.5 mb-3">
        <div className="h-5 w-10 bg-primary-100 dark:bg-primary-800 rounded" />
        <div className="h-5 w-14 bg-primary-100 dark:bg-primary-800 rounded" />
        <div className="h-5 w-12 bg-primary-100 dark:bg-primary-800 rounded" />
      </div>

      {/* Metrics row */}
      <div className="flex items-center gap-6 pt-3 border-t border-primary-100 dark:border-primary-800">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-primary-50 dark:bg-primary-800" />
          <div>
            <div className="h-2 w-10 bg-primary-100 dark:bg-primary-800 rounded mb-1" />
            <div className="h-4 w-8 bg-primary-100 dark:bg-primary-800 rounded" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-primary-50 dark:bg-primary-800" />
          <div>
            <div className="h-2 w-14 bg-primary-100 dark:bg-primary-800 rounded mb-1" />
            <div className="h-4 w-12 bg-primary-100 dark:bg-primary-800 rounded" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-primary-50 dark:bg-primary-800" />
          <div>
            <div className="h-2 w-12 bg-primary-100 dark:bg-primary-800 rounded mb-1" />
            <div className="h-4 w-16 bg-primary-100 dark:bg-primary-800 rounded" />
          </div>
        </div>
      </div>
    </div>
  </div>
))

RecipientSkeleton.displayName = 'RecipientSkeleton'

/**
 * Refetch indicator - subtle pulsing gradient bar at the top
 */
const RefetchIndicator: React.FC = memo(() => (
  <div className="absolute top-0 left-0 right-0 z-10 h-0.5 overflow-hidden">
    <div
      className="h-full w-full bg-gradient-to-r from-accent-400 via-accent-500 to-accent-400"
      style={{
        animation: 'pulse-glow 1.5s ease-in-out infinite',
      }}
    />
    <style jsx>{`
      @keyframes pulse-glow {
        0%,
        100% {
          opacity: 0.4;
        }
        50% {
          opacity: 1;
        }
      }
    `}</style>
  </div>
))

RefetchIndicator.displayName = 'RefetchIndicator'

/**
 * Pinned result card rendered above the virtualizer for exact identifier matches
 */
const PinnedRecipientCard: React.FC<{
  recipient: RecipientSearchResult
  className?: string
}> = memo(({ recipient, className }) => (
  <div className={twMerge('max-w-5xl mx-auto', className)}>
    <div className="flex items-center gap-2 mb-3">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-accent-600 dark:text-accent-400">
        Exact match
      </span>
      <div className="flex-1 h-px bg-primary-200/60 dark:bg-primary-700/60" />
    </div>
    <div className="rounded-xl p-2 ring-1 ring-accent-400/30 dark:ring-accent-500/30 bg-accent-50/30 dark:bg-accent-950/20">
      <RecipientCard recipient={recipient} className="w-full" />
    </div>
  </div>
))

PinnedRecipientCard.displayName = 'PinnedRecipientCard'

/**
 * Virtualized list of recipient search results
 */
const RecipientResults: React.FC<RecipientResultsProps> = ({
  results,
  total,
  isCountKnown = false,
  isLoading,
  isRefetching = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  onLoadMore,
  pinnedResult,
  className,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const loadMoreRef = useRef<HTMLDivElement>(null)

  // Deduplicate: filter pinned result out of the virtualized list
  const pinnedHash = pinnedResult?.recipient_hash ?? null
  const dedupedResults = useMemo(() => {
    if (!pinnedHash) return results
    return results.filter((r) => r.recipient_hash !== pinnedHash)
  }, [results, pinnedHash])

  // Virtualization setup - updated estimate for new card height
  const virtualizer = useVirtualizer({
    count: dedupedResults.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 165, // Reduced height for new compact design
    overscan: 4,
    getItemKey: (index) =>
      dedupedResults[index]?.recipient_hash || `skeleton-${index}`,
  })

  // Reset scroll position when search results change (rerender-dependencies)
  const firstResultHash = results[0]?.recipient_hash
  const prevFirstHashRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    // If the first result changed, it's a new search - scroll to top
    if (
      prevFirstHashRef.current !== undefined &&
      firstResultHash !== prevFirstHashRef.current
    ) {
      virtualizer.scrollToIndex(0, { behavior: 'auto', align: 'start' })
    }

    prevFirstHashRef.current = firstResultHash
  }, [firstResultHash, virtualizer])

  // Infinite scroll observer
  useEffect(() => {
    if (!hasNextPage || !onLoadMore || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore()
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1,
      },
    )

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => {
      observer.disconnect()
    }
  }, [hasNextPage, onLoadMore, isFetchingNextPage])

  const virtualItems = virtualizer.getVirtualItems()

  // Loading state - show skeleton on initial load
  if (isLoading && results.length === 0) {
    return (
      <div
        className={twMerge(
          'flex flex-col gap-3 p-4 bg-primary-50/50 dark:bg-primary-950',
          className,
        )}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <RecipientSkeleton key={i} />
        ))}
      </div>
    )
  }

  // Empty state - show when:
  // 1. Count is 0 and we have count data (not just defaulting)
  //    Count query uses placeholderData, so if previous count was 0, it stays 0
  // 2. Results are empty and we're fully done loading
  // Skip empty state if we have a pinned identifier match to show
  if (!pinnedResult) {
    if (!isLoading && isCountKnown && total === 0) {
      return <EmptyState />
    }
    if (!isLoading && !isRefetching && results.length === 0) {
      return <EmptyState />
    }
  }

  return (
    <div
      ref={parentRef}
      className={twMerge(
        'relative w-full h-full overflow-y-auto overflow-x-hidden',
        // Subtle gradient background for depth
        'bg-gradient-to-b from-primary-50/80 via-primary-50/50 to-primary-100/30',
        'dark:from-primary-950 dark:via-primary-950 dark:to-primary-900/50',
        className,
      )}
      style={{ contain: 'strict' }}
    >
      {/* Refetch indicator - subtle pulsing bar when filtering */}
      {isRefetching && <RefetchIndicator />}

      {/* Pinned exact-match result */}
      {pinnedResult && (
        <PinnedRecipientCard
          recipient={pinnedResult}
          className="px-4 pt-4 pb-3"
        />
      )}

      {/* Top padding for first card */}
      {!pinnedResult && <div className="h-4" />}

      <div
        className={twMerge(
          'relative w-full transition-opacity duration-200',
          isRefetching && 'opacity-50',
        )}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualItems.map((virtualItem) => {
          const recipient = dedupedResults[virtualItem.index]
          if (!recipient) return null

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              className="absolute top-0 left-0 w-full px-4 pb-3"
              style={{
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <RecipientCard
                recipient={recipient}
                className="w-full max-w-5xl mx-auto"
              />
            </div>
          )
        })}
      </div>

      {/* Load more trigger */}
      {hasNextPage && (
        <div
          ref={loadMoreRef}
          className="flex items-center justify-center py-8"
        >
          {isFetchingNextPage ? (
            <div className="flex items-center gap-2.5 px-4 py-2 bg-white dark:bg-primary-800 rounded-full shadow-sm border border-primary-200 dark:border-primary-700">
              <Spinner className="w-4 h-4 text-accent-500" />
              <span className="text-sm font-medium text-primary-600 dark:text-primary-300">
                Loading more...
              </span>
            </div>
          ) : (
            <span className="text-xs text-primary-400 dark:text-primary-500">
              Scroll for more
            </span>
          )}
        </div>
      )}

      {/* End of results */}
      {!hasNextPage && results.length > 0 && (
        <div className="flex items-center justify-center py-8">
          <span className="text-xs text-primary-400 dark:text-primary-500 tabular-nums">
            {total > 0
              ? `${results.length.toLocaleString()} of ${total.toLocaleString()} recipients`
              : `${results.length.toLocaleString()} recipients`}
          </span>
        </div>
      )}
    </div>
  )
}

export default memo(RecipientResults)
